import { useState } from 'react';
import { DragEndEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { updateFAQsSortOrder, FAQSortItem } from '../services/tree';

interface TreeNode {
  key: string;
  children?: TreeNode[];
  sort_order?: number;
}

interface UseFAQTreeDragSortProps<T extends TreeNode> {
  treeData: T[];
  setTreeData: (data: T[]) => void;
  datasetId: string;
}

export const useFAQTreeDragSort = <T extends TreeNode>({
  treeData,
  setTreeData,
  datasetId,
}: UseFAQTreeDragSortProps<T>) => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [sortLoading, setSortLoading] = useState<boolean>(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const getAllNodeKeys = (nodes: T[]): string[] => {
    const keys: string[] = [];
    const collectKeys = (nodeList: T[]) => {
      nodeList.forEach((node) => {
        keys.push(node.key);
        if (node.children?.length) {
          collectKeys(node.children as T[]);
        }
      });
    };
    collectKeys(nodes);
    return keys;
  };

  const findNodeById = (nodes: T[], id: string): T | null => {
    for (const node of nodes) {
      if (node.key === id) {
        return node;
      }
      if (node.children?.length) {
        const found = findNodeById(node.children as T[], id);
        if (found) return found;
      }
    }
    return null;
  };

  const findNodeParent = (nodeKey: string): string => {
    let parentKey = '0';

    const loop = (data: T[], parent: string = '0') => {
      data.forEach((item) => {
        if (item.key === nodeKey) {
          parentKey = parent;
          return;
        }
        if (item.children?.length) {
          loop(item.children as T[], item.key);
        }
      });
    };

    loop(treeData);
    return parentKey;
  };

  const isSameLevel = (activeId: string, overId: string): boolean => {
    const activeParent = findNodeParent(activeId);
    const overParent = findNodeParent(overId);
    return activeParent === overParent;
  };

  const reorderNodesInSameLevel = (
    data: T[],
    activeId: string,
    overId: string
  ): { updated: boolean; changedNodes: T[] } => {
    let updated = false;
    let changedNodes: T[] = [];

    const reorderLevel = (nodes: T[]): boolean => {
      const activeIndex = nodes.findIndex((node) => node.key === activeId);
      const overIndex = nodes.findIndex((node) => node.key === overId);

      if (activeIndex !== -1 && overIndex !== -1) {
        // 在当前层级找到了两个节点，进行排序
        const reorderedNodes = arrayMove(nodes, activeIndex, overIndex);
        // 更新原数组
        nodes.splice(0, nodes.length, ...reorderedNodes);
        changedNodes = [...reorderedNodes];
        return true;
      }

      // 递归检查子节点
      for (const node of nodes) {
        if (node.children?.length) {
          if (reorderLevel(node.children as T[])) {
            return true;
          }
        }
      }

      return false;
    };

    updated = reorderLevel(data);
    return { updated, changedNodes };
  };

  const updateSortOrderForChangedNodes = async (changedNodes: T[]) => {
    if (!datasetId || sortLoading || changedNodes.length === 0) return;

    const sortItems: FAQSortItem[] = [];

    // 只处理变化的节点，为它们分配新的sort_order
    changedNodes.forEach((node, index) => {
      if (node.key !== '0') {
        const newSortOrder = index + 1;
        const oldSortOrder = node.sort_order || 0;

        if (newSortOrder !== oldSortOrder) {
          sortItems.push({
            id: node.key,
            sort_order: newSortOrder,
          });
        }

        // 更新节点的 sort_order 以便下次对比
        node.sort_order = newSortOrder;
      }
    });

    if (sortItems.length === 0) return;

    setSortLoading(true);
    try {
      await updateFAQsSortOrder({
        datasetId,
        items: sortItems,
      });

      showNotification({
        message: t('faqs.tree.sort_success'),
        color: 'green.5',
      });
    } catch (error) {
      showNotification({
        message: t('faqs.tree.sort_failed'),
        color: 'red',
      });
    } finally {
      setSortLoading(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    // 检查是否在同一层级
    if (!isSameLevel(active.id as string, over.id as string)) {
      showNotification({
        message: t('faqs.tree.drag_same_level_only'),
        color: 'red',
      });
      setActiveId(null);
      return;
    }

    const newTreeData = [...treeData];
    const { updated, changedNodes } = reorderNodesInSameLevel(newTreeData, active.id as string, over.id as string);

    if (updated) {
      setTreeData(newTreeData);
      updateSortOrderForChangedNodes(changedNodes);
    }

    setActiveId(null);
  };

  return {
    // 状态
    activeId,
    sortLoading,
    sensors,

    // 方法
    getAllNodeKeys,
    findNodeById,
    handleDragStart,
    handleDragEnd,
  };
};
